import { Box } from '@mui/material';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    useActiveEditTarget,
    useActiveEditTargetSeriesUid,
} from '~/hooks/target/useActiveEditTarget';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { usePetMaxMarkerHost } from '~/hooks/target/usePetMaxMarkerHost';
import { useVoiBoxHost } from '~/hooks/target/useVoiBoxHost';
import { domainEventBus, RoiSelectionRequestedEvent } from '~/domain/events';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { CrosshairInteractionMode } from '~/engine/tool/ToolManager';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import { useHangingProtocolBinding } from '~/hooks/useHangingProtocolBinding';
import { usePrimaryRowApartBinding } from '~/hooks/usePrimaryRowPairView';
import TileImageMarks from './TileImageMarks';
import { useEnvironmentShowing } from '~/hooks/environment/useEnvironmentShowing';
import { useLandmarkVisibility } from '~/hooks/viewer/useLandmarkVisibility';
import { useImageRowsBinding } from '~/hooks/useImageRowsBinding';
import useViewportFocus from '~/hooks/useViewportFocus';
import { publishActiveTileRenderer } from '~/hooks/viewer/activeTileRenderer';
import { useContourDeletions } from '~/containers/viewer/contour-deletion/useContourDeletions';
import useWorkflowSteps from '~/containers/viewer/explorer-sidebar/WorkflowStepper/useWorkflowSteps';
import {
    viewManagerState,
    viewportReadyState,
} from '~/state/atoms/engineAtoms';
import {
    activeVoiConsumerState,
    scaleDisplayEnabledState,
    selectedToolState,
} from '~/state/atoms/toolsAtoms';
import {
    DatasetMetadata,
    DEFAULT_HANGING_PROTOCOL,
    EmptyViewportGrid,
    GridRowSpec,
    TileOverlayContext,
    RowOverlayContext,
    TileStatusContext,
    TileStructureActions,
    toDatasetRef,
    useAvailableDatasets,
    ViewportGridHost,
} from '~/viewportGrid';
import {
    BusWindowingAdapter,
    EnvironmentBackedEnvironmentAdapter,
    RegistryBlendAdapter,
    planeViewOf,
    RegistryDatasetAdapter,
    useGridEngineInput,
    useGridTeardownFlush,
    useGridToolArming,
} from '~/viewportGrid/adapters/engine';
import TileEngineOverlays from '~/viewportGrid/adapters/engine/overlays/TileEngineOverlays';
import TileStreamStatus from '~/viewportGrid/adapters/engine/overlays/TileStreamStatus';
import { useDosePanelTiles } from './viewport/useDosePanelTiles';
import { useImageOverviewWidget } from './viewport/useImageOverviewWidget';
import { usePetMipWidget } from './viewport/usePetMipWidget';
import { usePrimaryImageControls } from './viewport/usePrimaryImageControls';
import RowDoseControl from './viewport/rowDose/RowDoseControl';
import { useTileImageDetails } from './viewport/useTileImageDetails';
import { useStructurePreviewWidget } from './viewport/useStructurePreviewWidget';
import { seriesInstanceUidOf } from '~/domain/dicom/uids';
import type { RoiId } from '~/domain/changes/ids';

/**
 * CT rows first (the fixed image), then the rest, otherwise import order.
 * Also decides which dataset opens when the sidebar names no primary
 * image.
 */
function clinicalRowOrder(a: DatasetMetadata, b: DatasetMetadata): number {
    const rank = (meta: DatasetMetadata): number =>
        meta.modality === 'CT' ? 0 : 1;
    return rank(a) - rank(b);
}

/**
 * Embeddable engine-backed grid container: renders the grid host in a
 * relative, flow-sized column that fills its parent. Only the Viewer's
 * selected patient's datasets hang or bind: the dataset adapter serves that
 * patient alone, so another patient's imports never hang alongside. Carries the full engine effect stack (adapters +
 * rows, input attach, teardown flush, write arming, tool arming). Tools are
 * armed from the production step toolbar (the global selected tool) and the
 * sidebar ROI, not a grid-local strip. Two pieces of grid state leave the
 * grid: the active tile's renderer reaches a store, and the shape the rows
 * hang reaches a shared atom. The dose panels, when shown, hang as tiles on
 * the row that draws the dose. The overview, 3D preview and PET projection
 * float as widgets over their rows.
 */
export default function GridViewport() {
    const viewManager = useRecoilValue(viewManagerState);
    const selectedTool = useRecoilValue(selectedToolState);
    const activeVoiConsumer = useRecoilValue(activeVoiConsumerState);
    // The Pan tool's ruler toggle: the tiles draw their scale bar while on.
    const scaleBarVisible = useRecoilValue(scaleDisplayEnabledState);
    // The shape every row hangs, shared with the layout menu through the atom.
    const { hangingProtocol, onHangingChanged } = useHangingProtocolBinding();
    const { primaryRowApartRequest, onPrimaryRowApartChanged } =
        usePrimaryRowApartBinding();
    const { imageRowsRequest, onRowImagesChanged } = useImageRowsBinding();
    // The primary image is the dataset the sidebar names: the grid opens on
    // it, and the primary row follows it afterwards through the host.
    const primarySeriesUid = seriesInstanceUidOf(
        useSelectedPrimaryImage()?.seriesUid
    );
    // The primary row changes the primary image through the same switch as
    // the sidebar.
    const primaryImage = usePrimaryImageControls();
    // The active structure set's dataset is the editable set: only its tiles
    // are selectable/committable, and its tiles are what a double-click
    // focuses.
    const editableSeriesUid = useActiveEditTargetSeriesUid();
    const editTarget = useActiveEditTarget();
    const gridRootRef = useRef<HTMLDivElement>(null);
    // The host reads its rows once, when it mounts, and follows the primary
    // image and the hanging choice itself afterwards. Both are read through
    // refs, so a later change never builds rows the host ignores.
    const primarySeriesUidRef = useRef(primarySeriesUid);
    primarySeriesUidRef.current = primarySeriesUid;
    // The opening row hangs the shape last chosen, so the grid never mounts
    // the default and re-hangs.
    const hangingProtocolRef = useRef(hangingProtocol);
    hangingProtocolRef.current = hangingProtocol;
    const registry = getDatasetEnvironmentRegistry();
    const patient = useSelectedPatient();
    const domainStore = useSessionOrNull()?.context.store;
    const panels = useDosePanelTiles();
    const overviewWidgets = useImageOverviewWidget();
    const structurePreviewWidgets = useStructurePreviewWidget();
    const petMipWidgets = usePetMipWidget();
    const widgets = useMemo(
        () => [
            ...overviewWidgets,
            ...structurePreviewWidgets,
            ...petMipWidgets,
        ],
        [overviewWidgets, structurePreviewWidgets, petMipWidgets]
    );

    // Grid contour edits persist ONLY via an explicit flush: save every
    // resident Environment before the tile renderers release them.
    useGridTeardownFlush(registry, viewManager);

    // A patient open waits for a surface that can show what it loads. The
    // grid can while the engine is there: its rows bind the datasets the load
    // registers, whether or not any is registered when the open starts.
    const setViewportReady = useSetRecoilState(viewportReadyState);
    useEffect(() => {
        setViewportReady(viewManager !== null);
        return () => setViewportReady(false);
    }, [viewManager, setViewportReady]);

    const adapters = useMemo(() => {
        if (!viewManager) {
            return undefined;
        }
        return {
            datasetAdapter: new RegistryDatasetAdapter(
                registry,
                patient,
                domainStore
            ),
            environmentAdapter: new EnvironmentBackedEnvironmentAdapter(
                viewManager,
                registry
            ),
            windowingAdapter: new BusWindowingAdapter(registry),
            blendAdapter: new RegistryBlendAdapter(
                registry,
                patient,
                domainStore
            ),
        };
    }, [viewManager, registry, patient, domainStore]);

    // Live: an import, a load or a delete after the grid opened reaches the
    // row picker and the pin below.
    const datasets = useAvailableDatasets(adapters?.datasetAdapter);
    const available = useMemo(
        () => [...datasets].sort(clinicalRowOrder),
        [datasets]
    );

    const built = useMemo(() => {
        if (!adapters || available.length === 0) {
            return undefined;
        }
        // One row opens: the primary image, or the first in clinical order.
        // The rest stay bindable from the picker, so nothing hangs that the
        // user did not ask for.
        const opening =
            available.find(
                (meta) => meta.seriesInstanceUid === primarySeriesUidRef.current
            ) ?? available[0];
        const rows: GridRowSpec[] = [
            {
                dataset: toDatasetRef(opening),
                template:
                    hangingProtocolRef.current?.template ??
                    DEFAULT_HANGING_PROTOCOL.template,
            },
        ];
        return { ...adapters, rows };
    }, [adapters, available]);
    const hostMounted = built !== undefined;

    // Session pin: hold one extra reference per bindable dataset for the
    // grid's lifetime so layout changes (maximize/close/add/bind) never drop
    // a shared Environment to refcount zero and re-mint it. It covers every
    // dataset in scope, not just the opening row, because the binding path
    // takes no reference of its own. A dataset that joins is pinned and one
    // that leaves is released, each on its own: the datasets that stay keep
    // their pins, so none drops to zero, and loses an unflushed edit, on the
    // way. Declared after the teardown flush so, on unmount, the flush
    // cleanup runs before the release below: the pin only drops at zero
    // once edits are saved.
    const pinnedSeriesUids = hostMounted
        ? available.map((meta) => meta.seriesInstanceUid).join('\n')
        : '';
    const pinned = useRef(new Set<string>());
    useEffect(() => {
        const next = new Set(
            pinnedSeriesUids === '' ? [] : pinnedSeriesUids.split('\n')
        );
        // Each pin is recorded as it is taken or given back, so a throw part
        // way leaves `pinned` naming exactly the references held, and the
        // next run or the unmount releases each one once.
        const held = new Set(pinned.current);
        try {
            for (const seriesUid of next) {
                if (!held.has(seriesUid)) {
                    registry.acquire(seriesUid);
                    held.add(seriesUid);
                }
            }
            for (const seriesUid of pinned.current) {
                if (!next.has(seriesUid)) {
                    held.delete(seriesUid);
                    registry.release(seriesUid);
                }
            }
        } finally {
            pinned.current = held;
        }
    }, [pinnedSeriesUids, registry]);
    useEffect(
        () => () => {
            for (const seriesUid of pinned.current) {
                registry.release(seriesUid);
            }
            pinned.current = new Set();
        },
        [registry]
    );

    // Production tools drive the grid: route this root's events into the
    // viewer's interaction context (pan when no tool is armed yet).
    const interactionContext = useGridEngineInput(
        hostMounted ? viewManager : null,
        gridRootRef
    );
    // The contour deletions: the menu a still right-click or the hovered
    // structure's label opens, and the rectangle erase's drag.
    const contourDeletions = useContourDeletions(interactionContext);
    const { offersFor, openOverSelected } = contourDeletions;
    // The edited set's number of an ROI the grid names by id; undefined for
    // an ROI of any other set, which is then neither offered nor selected.
    const editedSet = editTarget.environment?.structureSet?.parent;
    const roiNumberOf = useCallback(
        (roiId: RoiId): number | undefined =>
            editedSet?.getRoiById(roiId)?.roiNumber,
        [editedSet]
    );
    const structureActions = useMemo<TileStructureActions>(
        () => ({
            offersFor: (roiId) => {
                const roiNumber = roiNumberOf(roiId);
                return roiNumber !== undefined && offersFor(roiNumber);
            },
            open: (renderer, pick, anchor) => {
                const view = planeViewOf(renderer);
                if (view) {
                    openOverSelected(view, pick, anchor);
                }
            },
        }),
        [offersFor, openOverSelected, roiNumberOf]
    );

    // Tool arming reads the active edit target (the sidebar's active structure
    // set resolved to its registry Environment + the sidebar ROI) and the
    // global selected tool set by the production step toolbar; a write tool
    // with an unresolved target keeps pan armed (never a dead no-tool state).
    // Undo/redo keys on the same target through the resolver — no grid-local
    // selection state exists.
    useGridToolArming(registry, viewManager);

    // Click-to-select an ROI: only in pan mode (a draw click is a stroke),
    // never while a region box is up (its double-click recentres the box,
    // and a select and focus under the same gesture would also zoom), and
    // only on editable-set tiles (enforced by the grid, which passes
    // onSelectRoi only for those). The picked ROI is named by id and
    // resolved in the active structure set, so an ROI of any other set
    // selects nothing. Publishing the request keeps the selectedROI write
    // in the domain handler (no atom write here).
    const canSelectRoi = selectedTool === 'pan' && activeVoiConsumer === null;
    const { focusOnStructure } = useViewportFocus();
    const onSelectRoi = useCallback(
        (roiId: RoiId, focus: boolean) => {
            const roiNumber = roiNumberOf(roiId);
            if (roiNumber === undefined) {
                return;
            }
            void domainEventBus.publish(
                new RoiSelectionRequestedEvent(roiNumber, 'user')
            );
            // Double-click also frames the editable set's tiles on the ROI.
            if (focus) {
                focusOnStructure(roiNumber);
            }
        },
        [roiNumberOf, focusOnStructure]
    );

    // While poi-move is armed, let the crosshairs yield so a POI drag doesn't
    // fight them (the marker's own hit area already keeps the press off the
    // canvas, so the armed tool never pans). One owner for the whole grid.
    const toolManager = viewManager?.interactionContext.toolManager;
    useEffect(() => {
        if (!toolManager) {
            return undefined;
        }
        toolManager.setExternalCrosshairMode(
            selectedTool === 'poi-move'
                ? CrosshairInteractionMode.RootsOnly
                : null
        );
        return () => toolManager.setExternalCrosshairMode(null);
    }, [toolManager, selectedTool]);
    const isActionInProgress = useCallback(
        () => toolManager?.isActiveToolActionInProgress() ?? false,
        [toolManager]
    );

    // One host per grid: the tiles compare their Environment to it.
    const voiHost = useVoiBoxHost();
    const petMaxMarkerHost = usePetMaxMarkerHost();
    const editTargetHost = editTarget.environment;
    const fusion = useActiveFusionTarget();
    // The registration keys act only in the registration step.
    const { currentStep } = useWorkflowSteps();
    const registrationHintHost =
        fusion.actionable && currentStep.id === 'registration'
            ? fusion.environment
            : undefined;
    const deformationFieldHost = useEnvironmentShowing(
        useSelectedPrimaryImage()
    );
    const landmarksVisible = useLandmarkVisibility();
    const tileOverlay = useCallback(
        (context: TileOverlayContext): ReactNode => (
            <TileEngineOverlays
                renderer={context.renderer}
                ledImages={context.ledImages}
                voiHost={voiHost}
                petMaxMarkerHost={petMaxMarkerHost}
                editTargetHost={editTargetHost}
                registrationHintHost={registrationHintHost}
                deformationFieldHost={deformationFieldHost}
                landmarksVisible={landmarksVisible}
            />
        ),
        [
            voiHost,
            petMaxMarkerHost,
            editTargetHost,
            registrationHintHost,
            deformationFieldHost,
            landmarksVisible,
        ]
    );
    // A tile's image name opens the image's details card on hover.
    const imageDetails = useTileImageDetails(registry);
    const rowOverlayControls = useCallback(
        (context: RowOverlayContext): ReactNode => (
            <RowDoseControl seriesInstanceUid={context.seriesInstanceUid} />
        ),
        []
    );
    const tileStatus = useCallback(
        (context: TileStatusContext): ReactNode => (
            <>
                <TileImageMarks images={context.images} />
                <TileStreamStatus
                    images={context.images}
                    leadsRow={context.leadsRow}
                    registry={registry}
                />
            </>
        ),
        [registry]
    );

    // With no dataset of the patient to show, the grid shows its shape with
    // empty panes; with no patient loaded, the drop area lies over it.
    if (!built) {
        return (
            <EmptyViewportGrid
                protocol={hangingProtocol ?? DEFAULT_HANGING_PROTOCOL}
            />
        );
    }

    return (
        <Box
            ref={gridRootRef}
            data-testid="grid-render-root"
            sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* The same bottom margin as the sidebar's framed panel, so the
                two end on one line, and as much again from the window's
                right edge. */}
            <Box
                data-testid="grid-rows"
                sx={{
                    position: 'relative',
                    flex: 1,
                    minHeight: 0,
                    mb: 1,
                    mr: 1,
                }}
            >
                {/* Another patient is another grid: no row of the patient
                    before stays on screen. The patient switch saves the
                    grid's contour edits before it selects the patient. */}
                <ViewportGridHost
                    key={patient?.id}
                    datasetAdapter={built.datasetAdapter}
                    environmentAdapter={built.environmentAdapter}
                    windowingAdapter={built.windowingAdapter}
                    blendAdapter={built.blendAdapter}
                    rows={built.rows}
                    tileOverlay={tileOverlay}
                    tileStatus={tileStatus}
                    imageDetails={imageDetails}
                    rowOverlayControls={rowOverlayControls}
                    panels={panels}
                    widgets={widgets}
                    primarySeriesUid={primarySeriesUid}
                    primaryImage={primaryImage}
                    editableSeriesUid={editableSeriesUid}
                    canSelectRoi={canSelectRoi}
                    onSelectRoi={onSelectRoi}
                    structureActions={structureActions}
                    hangingProtocol={hangingProtocol}
                    onHangingChanged={onHangingChanged}
                    imageRowsRequest={imageRowsRequest}
                    onRowImagesChanged={onRowImagesChanged}
                    onActiveRendererChanged={publishActiveTileRenderer}
                    isActionInProgress={isActionInProgress}
                    scaleBarVisible={scaleBarVisible}
                    primaryRowApartRequest={primaryRowApartRequest}
                    onPrimaryRowApartChanged={onPrimaryRowApartChanged}
                />
            </Box>
            {contourDeletions.menu}
        </Box>
    );
}
